import { HTMLAttributes } from 'react'

import { FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import { IntlFormatters } from 'react-intl'
import * as yup from 'yup'

import {
  CalculateCaloriesProps,
  calculateCalories,
} from 'components/pages/healthTools/helpers'
import {
  ACTIVITY_LEVEL_NAME,
  HealthToolGender,
} from 'interfaces/constants/healthTools'
import { LOCALE } from 'interfaces/types'

export type BmrItemProps = {
  isLoading?: boolean
  isDisabled?: boolean
  dataEventCategory?: string
  dataEventAction?: string
  dataEventLabel?: string
}
export type BmrFormValue = {
  gender: HealthToolGender
  birthday?: Date
  age?: string
  weight?: string
  height?: string
  bmr?: number
  activityLevel?: ACTIVITY_LEVEL_NAME
  isCheckYourself?: boolean
}
export type BmrFormValueKeys = keyof BmrFormValue
export type BmrFormProps = Omit<HTMLAttributes<HTMLDivElement>, 'onSubmrt'> & {
  onSubmit?: (value: BmrFormValue) => void
  template?: 'page' | 'embed'
}
export type Rule = Omit<
  RegisterOptions<FieldValues, FieldPath<FieldValues>>,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
>

export function convertLbsToKg(val: number): number {
  return val * 0.45359237
}

export function convertKgToLbs(val: number): number {
  return val / 0.45359237
}

export function convertFtToCm(val: number): number {
  return val * 30.48
}

export function convertCmToFt(val: number): number {
  return val / 30.48
}

export const BmrUnitValidationBase = {
  minAge: 18,
  maxAge: 100,
  minHeight: 1,
  maxHeight: 300,
  minWeight: 1,
  maxWeight: 500,
  heightConvert: (val: number): number => val,
  weightConvert: (val: number): number => val,
}
export type BmrUnitValidate = typeof BmrUnitValidationBase
export const bmrUnitValidation: Record<LOCALE, BmrUnitValidate> = {
  [LOCALE.PhilippinesEnglish]: BmrUnitValidationBase,
  [LOCALE.India]: BmrUnitValidationBase,
  [LOCALE.Indonesia]: BmrUnitValidationBase,
  [LOCALE.Cambodia]: BmrUnitValidationBase,
  [LOCALE.Malaysia]: BmrUnitValidationBase,
  // generate validation rules for MM: weight (lbs) & height (ft)
  // 1(ft) == 30.48(cm)
  // 1(lbs) == 0.45359237(kg)
  [LOCALE.Myanmar]: {
    ...BmrUnitValidationBase,
    minHeight: convertCmToFt(1),
    maxHeight: convertCmToFt(300),
    minWeight: convertKgToLbs(1),
    maxWeight: convertKgToLbs(500),
    heightConvert: convertFtToCm,
    weightConvert: convertLbsToKg,
  },
  [LOCALE.Thailand]: BmrUnitValidationBase,
  [LOCALE.PhilippinesTagalog]: BmrUnitValidationBase,
  [LOCALE.Vietnam]: BmrUnitValidationBase,
  [LOCALE.Taiwan]: BmrUnitValidationBase,
  [LOCALE.Singapore]: BmrUnitValidationBase,
}

// Function calculate bmr from weight unit (Kg) & height unit (cm)
export const getBmr = async (props: CalculateCaloriesProps): Promise<number> =>
  await calculateCalories(props).then((res) => res?.data?.bmr?.bmr || 0)

export const generateSchema = (
  f: IntlFormatters['formatMessage'],
  locale: LOCALE,
) => {
  return yup.object().shape({
    gender: yup.string().required(
      f({
        id: 'tools.genderInput.error',
        defaultMessage: 'Please select gender',
      }),
    ),
    birthday: yup.date().required(
      f({
        id: 'bmi.ageInput.error',
        defaultMessage: 'Please enter a valid age',
      }),
    ),
    // age: yup
    //   .number()
    //   .typeError(
    //     f({
    //       id: 'bmi.ageInput.error',
    //       defaultMessage: 'Please enter a valid age',
    //     }),
    //   )
    //   .required(
    //     f({
    //       id: 'bmi.ageInput.error',
    //       defaultMessage: 'Please enter a valid age',
    //     }),
    //   )
    //   .min(
    //     bmrUnitValidation[locale].minAge,
    //     f({
    //       id: 'tools.ageInput.limitError',
    //       defaultMessage: 'Please input your age in 18-100 ',
    //     }),
    //   )
    //   .max(
    //     bmrUnitValidation[locale].maxAge,
    //     f({
    //       id: 'tools.ageInput.limitError',
    //       defaultMessage: 'Please input your age in 18-100 ',
    //     }),
    //   ),
    height: yup
      .number()
      .typeError(
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      )
      .required(
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      )
      .min(
        bmrUnitValidation[locale].minHeight,
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      )
      .max(
        bmrUnitValidation[locale].maxHeight,
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      ),
    weight: yup
      .number()
      .typeError(
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      )
      .required(
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      )
      .min(
        bmrUnitValidation[locale].minWeight,
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      )
      .max(
        bmrUnitValidation[locale].maxWeight,
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      ),
  })
}
export const generateBmrRules: ({
  f,
  locale,
}: {
  f: IntlFormatters['formatMessage']
  locale: LOCALE
}) => Partial<Record<BmrFormValueKeys, { label: string; rule?: Rule }>> = ({
  f,
  locale,
}) => ({
  gender: {
    label: f({
      id: 'bmi.selectGender',
      defaultMessage: 'What is your gender?',
    }),
  },
  age: {
    label:
      f({
        id: 'bmi.ageInput',
        defaultMessage: 'How old are you?',
      }) +
      ' ' +
      f({
        id: 'bmi.ageInput.desc',
        defaultMessage: '(years)',
      }),
    rule: {
      required: f({
        id: 'bmi.ageInput.error',
        defaultMessage: 'Please enter a valid age',
      }),
      min: {
        value: bmrUnitValidation[locale].minAge,
        message: f({
          id: 'bmi.ageInput.error',
          defaultMessage: 'Please enter a valid age',
        }),
      },
      max: {
        value: bmrUnitValidation[locale].maxAge,
        message: f({
          id: 'bmi.ageInput.error',
          defaultMessage: 'Please enter a valid age',
        }),
      },
    },
  },
  height: {
    label: f({
      id: 'bmi.heightInput',
      defaultMessage: 'How tall are you?',
    }),
    rule: {
      required: f({
        id: 'bmi.heightInput.error',
        defaultMessage: 'Please enter a valid height',
      }),
      min: {
        value: bmrUnitValidation[locale].minHeight,
        message: f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      },
      max: {
        value: bmrUnitValidation[locale].maxHeight,
        message: f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      },
    },
  },
  weight: {
    label: f({
      id: 'bmi.weightInput',
      defaultMessage: 'How much do you weight?',
    }),
    rule: {
      required: f({
        id: 'bmi.weightInput.error',
        defaultMessage: 'Please enter a valid weight',
      }),
      min: {
        value: bmrUnitValidation[locale].minWeight,
        message: f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      },
      max: {
        value: bmrUnitValidation[locale].maxWeight,
        message: f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      },
    },
  },
  objective: {
    label: f({
      id: 'bmi.objective',
      defaultMessage: 'Objectives',
    }),
  },
  bmr: { label: '' },
  isCheckYourself: { label: '' },
  activityLevel: {
    label: f({ id: 'bmr.selectActivityLevel' }),
  },
})
